"use client";

import type { ImageProps } from "next/image";

import Image from "next/image";
import { useState } from "react";

import { cn } from "@/lib/utils";

type OptimizedImageProps = {
  alt: string;
  fallbackSrc?: string;
  className?: string;
  wrapperClassName?: string;
  rounded?: boolean;
  showSkeleton?: boolean;
  overflow?: boolean;
} & Omit<ImageProps, "alt">;

export function OptimizedImage({
  alt,
  fallbackSrc = "/fallback-image.png",
  className,
  wrapperClassName,
  rounded = true,
  overflow = true,
  showSkeleton = true,
  ...props
}: OptimizedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  return (
    <div
      className={cn(
        "relative",
        { "rounded-xl": rounded },
        { "overflow-hidden": overflow },
        wrapperClassName,
      )}
    >
      {showSkeleton && !isLoaded && (
        <div className="absolute inset-0 bg-muted animate-pulse rounded-xl" />
      )}

      <Image
        {...props}
        alt={alt}
        src={hasError ? fallbackSrc : props.src}
        onLoad={() => setIsLoaded(true)}
        onError={() => setHasError(true)}
        className={cn(
          "transition-opacity duration-500 ease-in-out",
          isLoaded ? "opacity-100" : "opacity-0",
          rounded && "rounded-xl",
          className,
        )}
      />
    </div>
  );
}
